/**
 * src/pages/Onboarding.jsx
 * Wizard post-pago de 3 pasos:
 *   1. Confirmar pago (polling Stripe)
 *   2. Configurar empresa (familia CM, empresa propia, seller code)
 *   3. Listo → ir al panel
 */
import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { getSuscripcion } from '../api/billing'
import api from '../api/client'

// Solo las tiendas que el comparador monitoriza HOY de verdad (ver P8). El resto
// del Convenio Marco está en investigación; no las ofrecemos hasta que funcionen.
const FAMILIAS_CM = [
  { value: 'alimentos2',  label: 'Alimentos',  url: 'https://conveniomarco2.mercadopublico.cl/alimentos2' },
  { value: 'ferreteria2', label: 'Ferretería', url: 'https://conveniomarco2.mercadopublico.cl/ferreteria2' },
  { value: 'otro',        label: 'Otra familia (consultar — aún no disponible)', url: '' },
]

const inputStyle = {
  width: '100%', background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)', borderRadius: '8px',
  padding: '10px 14px', fontSize: '14px', fontFamily: 'var(--font-ui)',
  color: 'var(--color-text)', outline: 'none', boxSizing: 'border-box',
  transition: 'border-color 0.15s',
}

function StepIndicator({ current, total }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', marginBottom: '32px' }}>
      {Array.from({ length: total }, (_, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: '12px',
            fontFamily: 'var(--font-mono)', fontWeight: 500,
            background: i < current ? 'var(--color-accent-dim)' : i === current ? 'var(--color-accent)' : 'var(--color-surface-2)',
            color: i < current ? 'var(--color-accent)' : i === current ? '#fff' : 'var(--color-text-3)',
            border: `1px solid ${i <= current ? 'rgba(16,185,129,0.3)' : 'var(--color-border)'}`,
          }}>
            {i < current ? '✓' : i + 1}
          </div>
          {i < total - 1 && (
            <div style={{ width: '40px', height: '1px', background: i < current ? 'var(--color-accent)' : 'var(--color-border)' }} />
          )}
        </div>
      ))}
    </div>
  )
}

// Paso 1: Confirmar pago
function Step1PagoConfirmado({ onNext }) {
  const [estado, setEstado] = useState('verificando')
  const [suscripcion, setSuscripcion] = useState(null)

  useEffect(() => {
    async function verificar() {
      for (let i = 0; i < 6; i++) {
        try {
          const data = await getSuscripcion()
          if (data?.tier) { setSuscripcion(data); setEstado('ok'); return }
        } catch {}
        await new Promise(r => setTimeout(r, 1500))
      }
      setEstado('pendiente')
    }
    verificar()
  }, [])

  return (
    <div style={{ textAlign: 'center' }}>
      <h2 style={{ fontSize: '22px', fontWeight: 700, marginBottom: '10px' }}>
        {estado === 'verificando' ? 'Confirmando tu pago...' :
         estado === 'ok' ? '¡Pago confirmado!' : 'Pago en proceso'}
      </h2>
      {estado === 'verificando' ? (
        <div style={{ margin: '28px 0' }}>
          <div style={{ width: '44px', height: '44px', border: '3px solid var(--color-border)', borderTopColor: 'var(--color-accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 14px' }} />
          <p style={{ color: 'var(--color-text-2)', fontSize: '14px' }}>Esto solo tardará un momento</p>
        </div>
      ) : (
        <>
          <div style={{ width: '52px', height: '52px', background: 'var(--color-accent-dim)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px', margin: '20px auto' }}>✓</div>
          {suscripcion && (
            <div style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '14px', margin: '16px 0', fontSize: '13px', color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)' }}>
              Plan <strong style={{ color: 'var(--color-accent)' }}>{suscripcion.tier}</strong> · 14 días de prueba gratis
            </div>
          )}
          {estado === 'pendiente' && <p style={{ color: 'var(--color-text-2)', fontSize: '13px', marginBottom: '12px' }}>Tu suscripción se activará en breve. Puedes continuar.</p>}
          <button onClick={onNext} style={{ background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: '8px', padding: '11px 28px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)', marginTop: '8px' }}>
            Configurar mi cuenta →
          </button>
        </>
      )}
    </div>
  )
}

// Paso 2: Configurar empresa
function Step2Configurar({ onNext }) {
  const user = useAuthStore(state => state.user)
  const [form, setForm] = useState({
    nombre_empresa: '',
    identificador: '',
    familia_cm: 'alimentos2',
    url_base: FAMILIAS_CM[0].url,
    empresa_rut: '',
    empresa_nombre: '',
    empresa_seller_code: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [hallazgo, setHallazgo] = useState(null)   // {empresa, seller_code} | {error}
  const [rutBuscado, setRutBuscado] = useState('') // último RUT ya verificado (evita repetir)

  // Autocompletar empresa + seller_code a partir del RUT (reusa F1 /confirmar).
  async function buscarEmpresa() {
    const rut = form.empresa_rut.trim()
    if (!rut) { setHallazgo({ error: 'Escribe tu RUT primero.' }); return }
    setRutBuscado(rut)
    setBuscando(true); setHallazgo(null)
    try {
      const { data } = await api.get('/api/diagnostico/confirmar', { params: { rut } })
      if (data?.encontrada && data?.seller_code) {
        setForm(f => ({
          ...f,
          empresa_rut:         data.rut || f.empresa_rut,
          empresa_nombre:      data.empresa || f.empresa_nombre,
          nombre_empresa:      f.nombre_empresa || data.empresa || '',
          empresa_seller_code: String(data.seller_code),
        }))
        setHallazgo({ empresa: data.empresa, seller_code: data.seller_code })
      } else {
        setHallazgo({ error: 'No encontramos esa empresa como proveedor del Estado. Revisa el RUT.' })
      }
    } catch (err) {
      setHallazgo({ error: 'No se pudo verificar el RUT ahora. Inténtalo de nuevo.' })
    } finally { setBuscando(false) }
  }

  function set(field) {
    return e => {
      const val = e.target.value
      setForm(f => {
        const next = { ...f, [field]: val }
        if (field === 'familia_cm') {
          const familia = FAMILIAS_CM.find(fm => fm.value === val)
          if (familia && familia.url) next.url_base = familia.url
        }
        return next
      })
    }
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (!form.nombre_empresa || !form.empresa_rut) {
      setError('Completa los campos obligatorios'); return
    }
    if (!form.empresa_seller_code) {
      setError('No hemos podido verificar tu empresa en Mercado Público — revisa el RUT y pulsa Verificar.')
      return
    }
    setLoading(true); setError('')
    try {
      const payload = {
        nombre_empresa:      form.nombre_empresa,
        identificador:       form.identificador.trim() || undefined,
        familia_cm:          form.familia_cm,
        url_base:            form.url_base,
        empresa_rut:         form.empresa_rut,
        empresa_nombre:      form.empresa_nombre || form.nombre_empresa,
        empresa_seller_code: form.empresa_seller_code,
      }
      const r = await api.post('/billing/onboarding', payload)
      onNext(r.data.cliente_id)
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al configurar la cuenta')
    } finally { setLoading(false) }
  }

  const labelStyle = { display: 'block', fontSize: '11px', fontWeight: 500, color: 'var(--color-text-2)', marginBottom: '5px', fontFamily: 'var(--font-mono)', letterSpacing: '0.05em' }

  return (
    <div>
      <h2 style={{ fontSize: '20px', fontWeight: 700, marginBottom: '6px' }}>Configura tu empresa</h2>
      <p style={{ color: 'var(--color-text-2)', fontSize: '14px', marginBottom: '24px' }}>Esta información configura tu monitorización en Mercado Público.</p>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

        <div>
          <label style={labelStyle}>NOMBRE DE EMPRESA *</label>
          <input style={inputStyle} value={form.nombre_empresa} onChange={set('nombre_empresa')}
            placeholder="Ej: Nutrihouse Limitada"
            onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
            onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
        </div>

        <div>
          <label style={labelStyle}>IDENTIFICADOR CORTO <span style={{ color: 'var(--color-text-3)', fontSize: '10px' }}>(opcional — se usa en la URL de tu dashboard)</span></label>
          <input style={inputStyle} value={form.identificador}
            onChange={e => setForm(f => ({ ...f, identificador: e.target.value.toLowerCase().replace(/[^a-z0-9_-\s]/g, '').replace(/[\s-]+/g, '_') }))}
            placeholder="ej: eda (si lo dejas vacío, lo generamos por ti)"
            maxLength={30}
            onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
            onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
        </div>

        <div>
          <label style={labelStyle}>FAMILIA CONVENIO MARCO *</label>
          <select value={form.familia_cm}
            onChange={e => {
              const fam = FAMILIAS_CM.find(x => x.value === e.target.value)
              setForm(f => ({ ...f, familia_cm: fam.value, url_base: fam.url }))
            }}
            style={{ ...inputStyle, cursor: 'pointer' }}>
            {FAMILIAS_CM.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
        </div>

        {form.familia_cm === 'otro' && (
          <div>
            <label style={labelStyle}>URL BASE DE LA TIENDA *</label>
            <input style={inputStyle} value={form.url_base} onChange={set('url_base')}
              placeholder="https://conveniomarco2.mercadopublico.cl/..."
              onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
              onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
          </div>
        )}

        <div style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '16px' }}>
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '12px' }}>EMPRESA EN CONVENIO MARCO</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <label style={labelStyle}>RUT * <span style={{ color: 'var(--color-text-3)', fontSize: '10px' }}>(verificamos tu empresa en Mercado Público)</span></label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input style={{ ...inputStyle, flex: 1 }} value={form.empresa_rut} onChange={set('empresa_rut')}
                  placeholder="76.000.000-0"
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); buscarEmpresa() } }}
                  onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
                  onBlur={e => {
                    e.target.style.borderColor = 'var(--color-border)'
                    const rut = form.empresa_rut.trim()
                    if (rut && rut !== rutBuscado && !buscando) buscarEmpresa()
                  }} />
                <button type="button" onClick={buscarEmpresa} disabled={buscando}
                  style={{ background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: '8px', padding: '0 16px', fontSize: '13px', fontWeight: 600, cursor: buscando ? 'wait' : 'pointer', fontFamily: 'var(--font-ui)', whiteSpace: 'nowrap' }}>
                  {buscando ? 'Verificando…' : 'Verificar'}
                </button>
              </div>
              {hallazgo?.empresa && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-ok)' }}>
                  ✓ {hallazgo.empresa} · código {hallazgo.seller_code}
                </div>
              )}
              {hallazgo?.error && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-error)' }}>
                  {hallazgo.error}
                </div>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: hallazgo?.error ? '1fr 1fr' : '1fr', gap: '10px' }}>
              <div>
                <label style={labelStyle}>NOMBRE LEGAL</label>
                <input style={inputStyle} value={form.empresa_nombre} onChange={set('empresa_nombre')}
                  placeholder="Razón social"
                  onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
                  onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
              </div>
              {/* El código proveedor se resuelve solo desde el RUT; el campo manual
                  únicamente aparece como salvavidas si la verificación falla. */}
              {hallazgo?.error && (
                <div>
                  <label style={labelStyle}>CÓDIGO PROVEEDOR <span style={{ color: 'var(--color-text-3)', fontSize: '10px' }}>(si lo conoces)</span></label>
                  <input style={inputStyle} value={form.empresa_seller_code} onChange={set('empresa_seller_code')}
                    placeholder="1234567"
                    onFocus={e => e.target.style.borderColor = 'var(--color-accent)'}
                    onBlur={e => e.target.style.borderColor = 'var(--color-border)'} />
                </div>
              )}
            </div>
          </div>
        </div>

        {error &&<div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', padding: '10px 12px', fontSize: '12px', color: 'var(--color-error)', fontFamily: 'var(--font-mono)' }}>{error}</div>}

        <button type="submit" disabled={loading} style={{ width: '100%', background: loading ? 'var(--color-accent-dim)' : 'var(--color-accent)', color: loading ? 'var(--color-accent)' : '#fff', border: 'none', borderRadius: '8px', padding: '12px', fontSize: '14px', fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-ui)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
          {loading ? <><span style={{ width: '14px', height: '14px', border: '2px solid currentColor', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Configurando...</> : 'Guardar configuración →'}
        </button>
      </form>
    </div>
  )
}

// Paso 3: Listo
const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000'

function Step3Listo({ clienteId }) {
  // Texto ESPEJO del correo de acuse de alta (backend, cuenta_email.py). El plazo
  // "en unas 24 horas" replica el default de ONBOARDING_ETA_TEXTO: si esa env
  // cambia en Railway, actualizar también esta frase para que pantalla y correo
  // sigan diciendo lo mismo.
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: '60px', height: '60px', background: 'var(--color-accent-dim)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '26px', margin: '0 auto 20px' }}>✅</div>
      <h2 style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.02em', marginBottom: '10px' }}>¡Listo! Recibimos tu alta.</h2>
      <p style={{ color: 'var(--color-text-2)', fontSize: '15px', marginBottom: '24px' }}>Estamos preparando tu dashboard con tu histórico de Convenio Marco — normalmente está listo en unas 24 horas. Te avisaremos por correo en cuanto esté operativo.</p>
      <div style={{ background: 'var(--color-surface-2)', border: '1px solid var(--color-border)', borderRadius: '8px', padding: '14px 18px', marginBottom: '24px', textAlign: 'left' }}>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', marginBottom: '8px' }}>TU ID DE CLIENTE</div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '16px', color: 'var(--color-accent)', fontWeight: 500 }}>{clienteId}</div>
        <div style={{ fontSize: '12px', color: 'var(--color-text-3)', marginTop: '4px' }}>Identifica tu cuenta ante soporte</div>
      </div>
      <button
        onClick={() => { window.location.href = `${API_BASE}/scraping/${clienteId}/dashboard` }}
        style={{ width: '100%', background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: '8px', padding: '13px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
        Ir a tu dashboard →
      </button>
      <p style={{ fontSize: '12px', color: 'var(--color-text-3)', marginTop: '10px' }}>
        Entra con el mismo email y contraseña de esta cuenta.
      </p>
    </div>
  )
}

// Componente principal
export default function Onboarding() {
  const [params] = useSearchParams()
  const sessionId = params.get('session_id')
  const [step, setStep] = useState(sessionId ? 0 : 1)  // 0=confirmar pago, 1=configurar, 2=listo
  const [clienteId, setClienteId] = useState(null)

  const STEP_LABELS = ['Confirmar pago', 'Configurar empresa', '¡Listo!']

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div style={{ width: '100%', maxWidth: '520px' }}>
        <div style={{ textAlign: 'center', marginBottom: '8px' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500, fontSize: '16px', color: 'var(--color-text)', marginBottom: '4px' }}>CM Monitor</div>
          <div style={{ fontSize: '12px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em' }}>CONFIGURACIÓN DE CUENTA</div>
        </div>
        <div style={{ marginTop: '24px', background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: '14px', padding: '32px', boxShadow: 'var(--shadow-lg)' }}>
          <StepIndicator current={step} total={3} />
          <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)', textAlign: 'center', marginBottom: '24px', letterSpacing: '0.08em' }}>
            {STEP_LABELS[step]}
          </div>
          {step === 0 && <Step1PagoConfirmado onNext={() => setStep(1)} />}
          {step === 1 && <Step2Configurar onNext={(id) => { setClienteId(id); setStep(2) }} />}
          {step === 2 && <Step3Listo clienteId={clienteId} />}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}
